import authApi from '@api/authApi';
import {
  createCache,
  createStore,
  InstanceCache,
  type CacheOptions,
  type Cancel,
  type ConnectionActions,
  type Store,
} from 'cross-state';
import { io, type Socket } from 'socket.io-client';

const pool = new InstanceCache<[scope?: string], Socket & { connectionState: Store<boolean> }>((scope) => {
  const url = `${config.BACKEND_MESSAGE_HUB}/${scope ?? 'global'}`;

  const ws = io(url, {
    query: {
      scope, // the scope query parameter is used by the load balancer to route the connection to the correct server
    },
    async auth(cb) {
      const { accessToken } = (await authApi.auth.get()) ?? {};
      if (!accessToken) {
        throw new Error('Not authenticated');
      }
      cb({ token: accessToken });
    },
    autoConnect: false,
    multiplex: false,
  });

  const connectionState = createStore(false, {
    effect(store) {
      const connect = () => {
        console.debug(`[${url}] connected`);
        store.set(true);
      };
      const reconnect_attempt = () => {
        console.debug(`[${url}] reconnect_attempt`);
        store.set(false);
      };
      const reconnect = () => {
        console.debug(`[${url}] reconnect`);
        store.set(true);
      };
      const disconnect = (reason: Socket.DisconnectReason) => {
        console.debug(`[${url}] disconnect`);
        store.set(false);

        if (reason === 'io server disconnect') {
          ws.connect();
        }
      };
      const onMessage = (topic: string, ...args: unknown[]) => {
        console.debug('[ws] received', topic, {
          get payload() {
            return args;
          },
        });
      };

      ws.connect();
      ws.on('connect', connect);
      ws.on('reconnect_attempt', reconnect_attempt);
      ws.on('reconnect', reconnect);
      ws.on('disconnect', disconnect);
      ws.onAny(onMessage);

      return () => {
        ws.disconnect();
        ws.off('connect', connect);
        ws.off('reconnect_attempt', reconnect_attempt);
        ws.off('reconnect', reconnect);
        ws.off('disconnect', disconnect);
        ws.offAny(onMessage);
      };
    },
  });

  return Object.assign(ws, {
    connectionState,
  });
});

export default function hubConnection<T, Args extends any[]>(
  options: (...args: Args) => {
    fetch: () => Promise<T>;
    scope: string | undefined;
    handlers: Record<string, (payload: unknown, connection: ConnectionActions<Promise<T>>) => void | Promise<void>>;
  },
  cacheOptions?: CacheOptions<T, Args>,
) {
  return createCache<T, Args>(
    (...args) =>
      async ({ connect }) => {
        const { fetch, scope, handlers } = options(...args);

        await connect((connection) => {
          const { updateIsConnected, updateValue } = connection;

          const ws = pool.get(scope);
          const handles: Cancel[] = [ws.connectionState.subscribe(updateIsConnected)];

          const onReconnect = () => updateValue(fetch());
          ws.on('reconnect', onReconnect);
          handles.push(() => ws.off('reconnect', onReconnect));

          for (const [topic, handler] of Object.entries(handlers)) {
            const listener = async (payload: unknown) => {
              try {
                await handler(payload, connection);
              } catch (error) {
                console.error('[ws] error', topic, error);
              }
            };

            ws.on(topic, listener);
            handles.push(() => ws.off(topic, listener));
          }

          return () => {
            for (const handle of handles) {
              handle();
            }
          };
        });

        return await fetch();
      },
    {
      invalidateOnWindowFocus: false,
      ...cacheOptions,
    },
  );
}
